import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store, select } from '@ngrx/store';

import * as Tokens from '@shared/core/tokens';
import * as Utils from '@shared/core/utils';

import { ConvergePaymentProviderService } from './paymentProviders/converge.payment-provider.shared.service';
import { CardConnectPaymentProviderService } from './paymentProviders/card-connect.payment-provider.shared.service';
import { PaymentExpressPaymentProviderService } from './paymentProviders/payment-express.payment-provider.shared.service';
import { FatZebraPaymentProviderService } from './paymentProviders/fat-zebra.payment-provider.shared.service';
import { AdyenPaymentProviderService } from './paymentProviders/adyen.payment-provider.shared.service';
import { StripePaymentProviderService } from './paymentProviders/stripe.payment-provider.shared.service';
import { GooglePayPaymentProviderService } from './paymentProviders/google-pay.payment-provider.shared.service';
import { ApplePayPaymentProviderService } from './paymentProviders/apple-pay.payment-provider.shared.service';
import { WindcavePaymentProviderService } from './paymentProviders/windcave.payment-provider.shared.service';

import * as actions from '@shared/state/actions';
import * as selectors from '@shared/state/selectors';

import { PaymentsMapper } from '@shared/core/mappers/payments.shared.mapper';

import { Observable, throwError } from 'rxjs';
import { catchError, flatMap, map, filter, take, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class PaymentsService {
    constructor(
        @Inject(Tokens.CONFIG_TOKEN) public config: OLO.Config,
        public httpClient: HttpClient,
        public store: Store<OLO.State>,
        public convergePaymentProviderService: ConvergePaymentProviderService,
        public cardConnectPaymentProviderService: CardConnectPaymentProviderService,
        public paymentExpressPaymentProviderService: PaymentExpressPaymentProviderService,
        public fatZebraPaymentProviderService: FatZebraPaymentProviderService,
        public adyenPaymentProviderService: AdyenPaymentProviderService,
        public stripePaymentProviderService: StripePaymentProviderService,
        public googlePayPaymentProviderService: GooglePayPaymentProviderService,
        public applePayPaymentProviderService: ApplePayPaymentProviderService,
        public windcavePaymentProviderService: WindcavePaymentProviderService,
    ) {}

    public requestAdyenPreconfigurationSetup(locationNo: number): Observable<OLO.DTO.AdyenSettingsResponse> {
        return this.store.pipe(
            select(selectors.getLoyaltyAppSettings),
            filter((appSettings) => appSettings.data !== null),
            take(1),
            switchMap((appSettings) => this.adyenPaymentProviderService.requestConfig(locationNo, appSettings.data?.AppSettings?.DefaultAdyenSettings)),
            catchError((ex) => {
                console.error('Unable to request config data for Adyen PP.', ex);

                return throwError(ex);
            }),
        );
    }

    public requestStripePreconfigurationSetup(locationNo: number): Observable<OLO.DTO.StripeSettingsResponse> {
        return this.stripePaymentProviderService.requestConfig(locationNo);
    }

    public requestCardTokenForDefaultPaymentProvider(
        cardData: OLO.CreditCards.CreditCardDetails,
        locationNo: number = null,
    ): Observable<APICommon.PaymentProviderDefaultConfigResponse> {
        return this.store.pipe(
            select(selectors.getLoyaltyAppSettings),
            filter((appSettings) => appSettings.data !== null),
            take(1),
            flatMap((appSettings) => {
                switch (true) {
                    case this.config.payments.baseProvider === OLO.Enums.PAYMENT_PROVIDER.CONVERGE &&
                        appSettings.data.AppSettings.DefaultConvergeSettings !== null &&
                        this.convergePaymentProviderService !== null:
                        return this.convergePaymentProviderService.requestCardToken$(cardData, locationNo, appSettings.data.AppSettings.DefaultConvergeSettings).pipe(
                            map((convergeResponse) => ({
                                token: convergeResponse.ssl_token,
                            })),
                        );

                    case this.config.payments.baseProvider === OLO.Enums.PAYMENT_PROVIDER.CARD_CONNECT &&
                        appSettings.data.AppSettings.DefaultCardConnectSettings !== null &&
                        this.cardConnectPaymentProviderService !== null:
                        return this.cardConnectPaymentProviderService.requestCardToken2$(cardData, locationNo).pipe(
                            map((cardConnectResponse) => ({
                                token: cardConnectResponse.token,
                            })),
                        );

                    case this.config.payments.baseProvider === OLO.Enums.PAYMENT_PROVIDER.PAYMENT_EXPRESS &&
                        appSettings.data.AppSettings.DefaultPaymentExpressSettings !== null &&
                        this.paymentExpressPaymentProviderService !== null:
                        return this.paymentExpressPaymentProviderService.requestCardToken(locationNo, appSettings.data.AppSettings.DefaultPaymentExpressSettings).pipe(
                            map((paymentExpressResponse) => ({
                                token: paymentExpressResponse.SessionToken,
                                directPostUrl: paymentExpressResponse.Url,
                            })),
                        );

                    case this.config.payments.baseProvider === OLO.Enums.PAYMENT_PROVIDER.FAT_ZEBRA &&
                        appSettings.data.AppSettings.DefaultFatZebraSettings !== null &&
                        this.fatZebraPaymentProviderService !== null:
                        return this.fatZebraPaymentProviderService.requestCardToken(locationNo, appSettings.data.AppSettings.DefaultFatZebraSettings).pipe(
                            map((fatZebraResponse) => ({
                                token: fatZebraResponse.Verification,
                                directPostUrl: fatZebraResponse.DirectPostUrl,
                                returnUrlAfterRedirect: fatZebraResponse.ReturnPath,
                            })),
                        );

                    default:
                        return throwError('Default payment provider not configured');
                }
            }),
            catchError((ex) => {
                console.error('Unable to get card token', ex);

                return throwError(ex);
            }),
        );
    }

    public payWithAccountCharge(orderId: number, requestModel: OLO.DTO.ExecuteOnlineOrderAccountChargeModel): Observable<OLO.DTO.ExecuteAccountChargeResponse> {
        const mapedRequestModel: APIv3.AccountChargeRequest = PaymentsMapper.mapPayWithAccountChargePOSTRequest(requestModel);

        return this.httpClient
            .post<APIv3.AccountChargeResponse>(`${Utils.HTTP.switchApi(this.config.api.base)}/OnlineOrders/${orderId}/accountCharge`, mapedRequestModel)
            .pipe(map((response) => PaymentsMapper.mapPayWithAccountChargePOSTResponse(response)));
    }

    public pay(orderId: number, requestModel: OLO.DTO.ExecutePaymentModel): Observable<OLO.DTO.ExecutePaymentResponse> {
        const mapedRequestModel: APIv3.ExecutePaymentModel = PaymentsMapper.mapPayPOSTRequest(requestModel);

        return this.httpClient
            .post<APIv3.ExecuteTransactionResponse>(`${Utils.HTTP.switchApi(this.config.api.base)}/OnlineOrders/${orderId}/pay`, mapedRequestModel)
            .pipe(map((response) => PaymentsMapper.mapPayPOSTResponse(response)));
    }

    public getPaymentStatus(transactionId: string): Observable<OLO.DTO.GetTransactionResponse> {
        return this.httpClient
            .get<APIv3.GetTransactionResponse>(`${Utils.HTTP.switchApi(this.config.api.base)}/Payments/${transactionId}`)
            .pipe(map((response: APIv3.GetTransactionResponse) => PaymentsMapper.maPaymentStatusGETResponse(response)));
    }

    public resetPaymentFlow(): void {
        this.store.dispatch(actions.PaymentReset());
    }

    public cleanUp(): void {
        /* When exiting checkout page, make sure online order, payment and credit card data is reset */
        this.store.dispatch(actions.OnlineOrderStateReset());
        this.store.dispatch(actions.CreditCardTokenDataReset());
        this.store.dispatch(actions.PaymentReset());
    }

    public get defaultVendorPaymentProvider(): Nullable<OLO.Enums.PAYMENT_VENDOR_SERVICE> {
        const _f = Utils.VendorPayments;
        switch (true) {
            case _f.clientCanUseApplePayMethod(this.config):
                return OLO.Enums.PAYMENT_VENDOR_SERVICE.APPLE_PAY;
            case _f.isAppConfiguredForGooglePay(this.config):
                return OLO.Enums.PAYMENT_VENDOR_SERVICE.GOOGLE_PAY;
            case _f.isAppConfiguredForPayInStore(this.config):
                return OLO.Enums.PAYMENT_VENDOR_SERVICE.PAY_IN_STORE;
            default:
                return null;
        }
    }

    public get isAnyVendorPaymentProviderAvailable(): boolean {
        return Utils.VendorPayments.isAnyVendorPaymentMethodAvailable(this.config);
    }
}
